/**
 * The walk moved by a finger, as the app moves it (Unity `CameraControl`: WalkGestureStrategy, WalkSpeedController,
 * WalkRoomNavigation, WalkFramingGeometry): the stick's pace, what two fingers mean, the step a pinch takes, the slide
 * a drag leaves behind, and where a double tap takes the walker. Pure geometry; the Viewer applies it, through the
 * same collision as the keys.
 */
import type { Item, Level, Opening, Vec2, Wall } from '../core/hp/types';
import { isCarpetLike } from '../core/hp/furnitureClass';
import { isDoorPortal, moveWalker, type OpeningTags } from '../core/geometry/walkCollision';
import { isCurtain } from './curtains';
import { openingTwins } from '../core/geometry/openingTwins';
import { containsPoint } from './sceneGesture';

/** A held stick walks at 1 m/s and speeds up to 2 m/s over two seconds (`WalkSpeedController`: Lerp(1, 2, (t/2)²)). */
export function stickPace(seconds: number): number {
  const t = Math.min(1, Math.max(0, seconds) / 2);
  return 1 + t * t;
}

// ---- Two fingers --------------------------------------------------------------------------------------------------

export type TwoFingerMode = 'pan' | 'pinch' | 'twist';

/** What the fingers did since they touched, in pixels: the centre's travel, the span's change, the twist's arc. */
export interface TwoFingerEvidence { pan: number; pinch: number; twist: number; twistAngle: number }

/** Below this much evidence nothing is decided yet. */
const TWO_FINGER_SLOP = 12;
/** A twist needs more than 7° to turn the view (`TwoFingerGesture`). */
const TWIST_MIN_ANGLE = 7 * Math.PI / 180;

/**
 * Two fingers start as a drag; a pinch or a twist takes over once it is half as strong again as the others
 * (`TwoFingerGesture`'s 1.5 ratio), and a pinch or a twist, once taken, stays for the gesture.
 */
export function twoFingerMode(evidence: TwoFingerEvidence, current: TwoFingerMode | null): TwoFingerMode | null {
  if (current === 'pinch' || current === 'twist') return current;
  const { pan, pinch, twist } = evidence;
  if (Math.max(pan, pinch, twist) < TWO_FINGER_SLOP) return current;
  if (pinch >= 1.5 * Math.max(pan, twist)) return 'pinch';
  if (twist >= 1.5 * Math.max(pan, pinch) && evidence.twistAngle > TWIST_MIN_ANGLE) return 'twist';
  return 'pan';
}

/** A two-finger drag grabs the floor: sideways 5 mm and forward 1 cm per CSS pixel, as the app's drag at its room zoom. */
export const PAN_SIDEWAYS = .005;
export const PAN_FORWARD = .01;
/** Looking further up or down than this, a drag does not walk (`WalkGestureStrategy.AllowAerialDrag`). */
export const PAN_MAX_PITCH = Math.PI / 4;

/** The walk a drag of the fingers' centre makes, in the view's frame: fingers down walk forward, left step right. */
export function panStep(dx: number, dy: number): { fwd: number; strafe: number } {
  return { fwd: dy * PAN_FORWARD, strafe: -dx * PAN_SIDEWAYS };
}

/**
 * A pinch steps along the view toward what lies between the fingers: `d·(1 − 1/R)` for the span's ratio R (0.2–5)
 * and the distance d to the surface under the fingers (0.3–20 m, 3 m when nothing is hit) — spreading them twice as far
 * walks halfway there (`WalkZoomGeometry`).
 */
export function pinchStep(distance: number | null, ratio: number): number {
  const d = distance === null || !Number.isFinite(distance) ? 3 : Math.min(20, Math.max(.3, distance));
  const r = Math.min(5, Math.max(.2, Number.isFinite(ratio) && ratio > 0 ? ratio : 1));
  return d * (1 - 1 / r);
}

// ---- The slide after a drag ----------------------------------------------------------------------------------------

/** Where the walker was carried by a drag, and when (ms). */
export interface PanSample { at: number; x: number; z: number }

/** The last drag's pace, m/s in plan space; nothing when the fingers rested 130 ms before letting go (`CameraPanInertia`). */
export function coastVelocity(samples: readonly PanSample[], now: number): Vec2 | null {
  const recent = samples.filter(sample => now - sample.at <= 100);
  const last = samples[samples.length - 1];
  if (!last || now - last.at >= 130 || recent.length < 2) return null;
  const first = recent[0], span = (last.at - first.at) / 1000;
  if (span <= 0) return null;
  let x = (last.x - first.x) / span, z = (last.z - first.z) / span;
  const speed = Math.hypot(x, z);
  if (speed < .05) return null;
  if (speed > 3) { x *= 3 / speed; z *= 3 / speed; }
  return { x, z };
}

/** A drag's slide slows by 6% a 60 Hz frame (`CameraPanInertia`), the wheel's by 5% (`ZoomByWalk`), whatever the frame rate. */
export const DRAG_FRICTION = .94, WHEEL_FRICTION = .95;
export function coastDecay(velocity: Vec2, dt: number, friction = DRAG_FRICTION): Vec2 | null {
  const factor = friction ** (Math.max(0, dt) * 60);
  const next = { x: velocity.x * factor, z: velocity.z * factor };
  return Math.hypot(next.x, next.z) < .03 ? null : next;
}

// ---- A double tap --------------------------------------------------------------------------------------------------

export interface WalkPose { x: number; z: number; yaw: number; pitch: number }

export type WalkTapped =
  | { kind: 'floor' | 'ceiling'; point: { x: number; y: number; z: number } }
  | { kind: 'wall'; wall: Wall; point: { x: number; y: number; z: number } }
  | { kind: 'opening'; opening: Opening; wall: Wall; point: { x: number; y: number; z: number } }
  | { kind: 'item'; item: Item; point: { x: number; y: number; z: number } };

export interface WalkTapContext {
  level: Level;
  tags?: OpeningTags;
  /** The walker now; `eye` is its eye's height over the world's zero. */
  walker: WalkPose & { eye: number };
  /** The floor under the walker, over the world's zero. */
  floor: number;
  /** The walk camera's fields of view, radians. */
  hfov: number;
  vfov: number;
  /** The last double tap that framed something, and where the walker stood before it: tapping it again goes back. */
  approach: { key: string; from: WalkPose; at: WalkPose } | null;
}

export interface WalkDestination {
  pose: WalkPose;
  /** What this walk framed, for a second double tap; null when there is nothing to come back from. */
  approach: { key: string; from: WalkPose; at: WalkPose } | null;
}

/** Walk up to this far short of a tapped point (`WalkRoomNavigation.MetersBeforeFurniture`). */
const METRES_BEFORE = 1;
/** Furniture a double tap faces from the front (`FaceOrientedFurnitureTags`). */
const FACE_TAGS = new Set(['Sofa', 'Lounge', 'RightSofa', 'CornerSofa', 'Desk', 'Armchair', 'DressingTable', 'FloorMirror']);

const headingYaw = (x: number, z: number) => Math.atan2(x, z);
/** Still where the last double tap put it — a glance round since then, or the turn a selection makes, does not count. */
const stillThere = (a: WalkPose, b: WalkPose) => Math.hypot(a.x - b.x, a.z - b.z) < .3;

/** Whether the app would frame this piece from its front rather than walk up to where it was tapped (`RequireFaceView`). */
export function facesFront(item: Item): boolean {
  if (item.placement === 'wall' || isCurtain(item)) return true;
  if (isCarpetLike(item)) return false;
  if (item.tags?.some(tag => FACE_TAGS.has(tag))) return true;
  const long = Math.max(item.size.w, item.size.d), short = Math.min(item.size.w, item.size.d);
  return short > 0 && long / short > 1.4;
}

/** The distance that fits a stretch of this width and height into the walk's view, at least a metre. */
export function fitDistance(width: number, height: number, hfov: number, vfov: number): number {
  const across = width / 2 / Math.tan(Math.max(.2, hfov) / 2), up = height / 2 / Math.tan(Math.max(.2, vfov) / 2);
  return Math.max(METRES_BEFORE, across, up);
}

function roomOf(level: Level, point: Vec2): number | null {
  return level.rooms.find(room => containsPoint(room.polygon, point))?.index ?? null;
}

/** Backs off from a face along its normal as far as `distance`, stopping short of any wall on the way. */
function backOff(level: Level, face: Vec2, normal: Vec2, distance: number, tags?: OpeningTags): Vec2 {
  const start = { x: face.x + normal.x * .3, z: face.z + normal.z * .3 };
  const reach = Math.max(0, distance - .3);
  return moveWalker(level, start, { x: normal.x * reach, z: normal.z * reach }, tags);
}

/**
 * Where a double tap takes the walker (`WalkRoomNavigation.PanToController`): the floor, the ceiling or a small piece —
 * up to a metre short of the point, looking at it level; a wall — back from it far enough to see it whole, facing it;
 * a sofa, a desk, a long piece or a wall piece — in front of it the same way; a door — a metre before it, and a second
 * double tap on it walks through; the same wall or piece again — back where the walker stood. Null: stay.
 */
/** A doorway the demo walkthrough offers to enter: the door, its wall, its middle on the plan and the rooms either side. */
export interface PassableDoorway { opening: Opening; wall: Wall; centre: Vec2; front: Level['rooms'][number]; back: Level['rooms'][number] }

/**
 * The doors and arches of a floor that lead from one room to another, one per doorway — a door an app's project lists on
 * both rooms' copies of their wall counts once (`openingTwins`) — as the app's EnterRoomDoorMarkers.GetPassableDoors
 * finds them for its demo walkthrough: a walk-through opening (`isDoorPortal`) with a room on each side.
 */
export function passableDoorways(level: Level, tags?: OpeningTags): PassableDoorway[] {
  const twins = openingTwins(level), result: PassableDoorway[] = [];
  for (const opening of level.openings) {
    if (twins.has(opening.id) || !isDoorPortal(opening, tags)) continue;
    const wall = level.walls.find(value => value.id === opening.wallId);
    if (!wall || wall.hidden) continue;
    const centre = { x: wall.start.x + wall.dir.x * opening.offset, z: wall.start.z + wall.dir.z * opening.offset };
    const roomAt = (sign: number) => {
      const reach = (wall.thickness / 2 + .2) * sign, probe = { x: centre.x + wall.normalIn.x * reach, z: centre.z + wall.normalIn.z * reach };
      return level.rooms.filter(room => room.polygon.length >= 3 && containsPoint(room.polygon, probe)).sort((a, b) => a.area - b.area)[0];
    };
    const front = roomAt(1), back = roomAt(-1);
    if (front && back && front !== back) result.push({ opening, wall, centre, front, back });
  }
  return result;
}

/**
 * Where the walker stands once through a door, as the app's EnterThroughDoor leaves the walk camera
 * (`WalkRoomNavigation.PanToController`): a metre into the room on the other side of the door from it, looking on.
 */
export function throughDoor(wall: Pick<Wall, 'start' | 'dir' | 'normalIn' | 'thickness'>, opening: Pick<Opening, 'offset'>, walker: Vec2): WalkPose {
  const centre = { x: wall.start.x + wall.dir.x * opening.offset, z: wall.start.z + wall.dir.z * opening.offset };
  const side = Math.sign((walker.x - centre.x) * wall.normalIn.x + (walker.z - centre.z) * wall.normalIn.z) || 1;
  const beyond = wall.thickness / 2 + METRES_BEFORE, onward = { x: -wall.normalIn.x * side, z: -wall.normalIn.z * side };
  return { x: centre.x + onward.x * beyond, z: centre.z + onward.z * beyond, yaw: headingYaw(onward.x, onward.z), pitch: 0 };
}

export function walkTarget(tapped: WalkTapped, context: WalkTapContext): WalkDestination | null {
  const { level, walker, tags } = context;
  const here = { x: walker.x, z: walker.z };
  const again = (key: string) => context.approach && context.approach.key === key && stillThere(context.approach.at, walker) ? context.approach : null;
  const framed = (key: string, pose: WalkPose): WalkDestination => ({ pose, approach: { key, from: { x: walker.x, z: walker.z, yaw: walker.yaw, pitch: walker.pitch }, at: pose } });

  if (tapped.kind === 'opening' && isDoorPortal(tapped.opening, tags)) {
    const { opening, wall } = tapped, key = 'opening:' + opening.id;
    const centre = { x: wall.start.x + wall.dir.x * opening.offset, z: wall.start.z + wall.dir.z * opening.offset };
    const side = Math.sign((walker.x - centre.x) * wall.normalIn.x + (walker.z - centre.z) * wall.normalIn.z) || 1;
    const beyond = wall.thickness / 2 + METRES_BEFORE;
    // Through the door: a metre into the room on its other side, looking on.
    if (again(key)) return { pose: throughDoor(wall, opening, here), approach: null };
    const back = { x: wall.normalIn.x * side, z: wall.normalIn.z * side };
    return framed(key, { x: centre.x + back.x * beyond, z: centre.z + back.z * beyond, yaw: headingYaw(-back.x, -back.z), pitch: 0 });
  }

  if (tapped.kind === 'wall' || tapped.kind === 'opening' || (tapped.kind === 'item' && facesFront(tapped.item))) {
    const key = tapped.kind === 'item' ? 'item:' + tapped.item.id : tapped.kind === 'wall' ? 'wall:' + tapped.wall.id : 'opening:' + tapped.opening.id;
    const previous = again(key);
    if (previous) return { pose: previous.from, approach: null };
    if (tapped.kind === 'wall' || tapped.kind === 'opening') {
      const wall = tapped.wall;
      const side = Math.sign((walker.x - wall.center.x) * wall.normalIn.x + (walker.z - wall.center.z) * wall.normalIn.z) || 1;
      const normal = { x: wall.normalIn.x * side, z: wall.normalIn.z * side };
      const along = tapped.kind === 'opening' ? tapped.opening.offset : wall.length / 2;
      const width = tapped.kind === 'opening' ? tapped.opening.width + 1 : wall.length;
      const height = tapped.kind === 'opening' ? tapped.opening.height + .6 : wall.height;
      const face = { x: wall.start.x + wall.dir.x * along + normal.x * wall.thickness / 2, z: wall.start.z + wall.dir.z * along + normal.z * wall.thickness / 2 };
      const spot = tapped.kind === 'opening' ? { x: face.x + normal.x * METRES_BEFORE, z: face.z + normal.z * METRES_BEFORE }
        : backOff(level, face, normal, fitDistance(width, height, context.hfov, context.vfov), tags);
      return framed(key, { ...spot, yaw: headingYaw(-normal.x, -normal.z), pitch: 0 });
    }
    const item = tapped.item;
    let front = { x: -Math.sin(item.rotation), z: Math.cos(item.rotation) };
    // A piece that stands free is framed from the side of it the walker sees, to the nearest quarter turn: the app
    // does not walk round a table to its «front» (`WalkRoomNavigation`, «Fix rotation 360»).
    if (item.placement !== 'wall') {
      const toWalker = { x: walker.x - item.position.x, z: walker.z - item.position.z };
      const angle = Math.atan2(front.x * toWalker.z - front.z * toWalker.x, front.x * toWalker.x + front.z * toWalker.z);
      if (Math.abs(angle) > 100 * Math.PI / 180) {
        const turn = Math.round(angle / (Math.PI / 2)) * Math.PI / 2, c = Math.cos(turn), s = Math.sin(turn);
        front = { x: front.x * c - front.z * s, z: front.x * s + front.z * c };
      }
    }
    const depth = item.size.d, reach = fitDistance(item.size.w, item.size.h, context.hfov, context.vfov) + depth / 2;
    const surface = { x: item.position.x + front.x * depth / 2, z: item.position.z + front.z * depth / 2 };
    const spot = backOff(level, surface, front, reach - depth / 2, tags);
    const middle = context.floor + item.elevation + item.size.h / 2, rise = middle - walker.eye;
    const distance = Math.hypot(item.position.x - spot.x, item.position.z - spot.z);
    // Close to eye height the view stays level, with no tilt to distort it; far from it the walker looks at the piece.
    const pitch = Math.abs(rise) < .6 ? 0 : Math.atan2(rise, Math.max(.3, distance));
    return framed(key, { ...spot, yaw: headingYaw(-front.x, -front.z), pitch });
  }

  // The floor, the ceiling or a small piece: up to a metre short, in the same room, looking at it level.
  const point = { x: tapped.point.x, z: tapped.point.z };
  const away = { x: here.x - point.x, z: here.z - point.z }, length = Math.hypot(away.x, away.z);
  const yaw = length > 1e-6 ? headingYaw(-away.x, -away.z) : walker.yaw;
  if (level.rooms.length && roomOf(level, point) === null) return null;
  const before = length > 1e-6 ? { x: point.x + away.x / length * METRES_BEFORE, z: point.z + away.z / length * METRES_BEFORE } : point;
  const goal = roomOf(level, before) === roomOf(level, point) ? before : point;
  // A point beside a wall is reached as close as the body lets it.
  const spot = moveWalker(level, point, { x: goal.x - point.x, z: goal.z - point.z }, tags);
  return { pose: { x: spot.x, z: spot.z, yaw, pitch: 0 }, approach: null };
}

/** The app's flight to a double-tapped place: one second, easing in and out (`CameraHelper`, EaseInOutQuint). */
export const WALK_FLIGHT_MS = 1000;
export function easeInOutQuint(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x < .5 ? 16 * x ** 5 : 1 - (-2 * x + 2) ** 5 / 2;
}
